/* Restore a collection history entry, including products loaded with Show more.
 * Keep snapshots in this tab only; never restore them on a fresh visit/reload.
 */
(() => {
  const container = document.getElementById('ProductGridContainer');
  if (!container || !location.pathname.startsWith('/collections/')) return;
  const prefix = 'godly:collection:v1:';
  const maxAge = 30 * 60 * 1000;
  const url = () => location.pathname + location.search;
  const navigation = performance.getEntriesByType('navigation')[0];
  let restored = null;
  let memorySnapshot = null;
  let productDeparture = null;
  let stopRestore = () => {};

  function valid(saved) {
    return saved && saved.url === url() && Date.now() - saved.time < maxAge &&
      typeof saved.html === 'string' && Number.isFinite(saved.y);
  }

  function readSnapshot() {
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(entryKey()) || 'null'); }
    catch (error) { /* The live page can still use its in-memory snapshot. */ }
    if (valid(saved)) return saved;
    return valid(memorySnapshot) ? memorySnapshot : null;
  }

  // Capture before navigation, while the outgoing page still has its position.
  container.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
    const target = new URL(link.href, location.href);
    if (target.origin !== location.origin || !target.pathname.includes('/products/')) return;
    productDeparture = {
      url: url(), time: Date.now(), y: window.scrollY,
      anchor: link.getAttribute('href'), offset: link.getBoundingClientRect().top
    };
    saveSnapshot();
  });

  function restorePosition(saved) {
    stopRestore();
    const timers = [];
    let stopped = false;
    const inputs = ['touchstart', 'pointerdown', 'wheel', 'keydown'];
    const stop = () => {
      stopped = true;
      timers.forEach(clearTimeout);
      inputs.forEach(name => window.removeEventListener(name, stop));
      window.removeEventListener('popstate', stop);
    };
    stopRestore = stop;
    inputs.forEach(name => window.addEventListener(name, stop, { passive: true }));
    window.addEventListener('popstate', stop);
    const correct = () => {
      if (stopped || saved.url !== url()) return;
      let top = saved.y;
      // Keep the viewed product in place if mobile layout shifts after pageshow.
      if (saved.anchor && Number.isFinite(saved.offset)) {
        const link = Array.from(container.querySelectorAll('a[href]'))
          .find(item => item.getAttribute('href') === saved.anchor);
        if (link) top = window.scrollY + link.getBoundingClientRect().top - saved.offset;
      }
      if (Math.abs(window.scrollY - top) > 2) {
        window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' });
      }
    };
    // iOS may apply its own history scroll after pageshow. Retry briefly, but
    // stop immediately when the visitor touches, scrolls or navigates the page.
    [0, 80, 200, 400, 800, 1200].forEach(delay => timers.push(setTimeout(correct, delay)));
    timers.push(setTimeout(stop, 1300));
  }

  function entryKey() {
    let id = history.state?.godlyCollectionId;
    if (!id) {
      id = Date.now().toString(36) + Math.random().toString(36).slice(2);
      // facets.js expects searchParams in non-null history states.
      history.replaceState({
        ...history.state,
        searchParams: location.search.slice(1),
        godlyCollectionId: id
      }, '', location.href);
    }
    return prefix + id;
  }

  try {
    entryKey();
    if (navigation?.type === 'back_forward') {
      const saved = readSnapshot();
      if (saved) {
        container.innerHTML = saved.html;
        // A cached animation must not leave restored cards invisible.
        container.querySelectorAll('.scroll-trigger').forEach(element => {
          element.classList.add('scroll-trigger--cancel');
        });
        restored = saved;
      }
    }
  } catch (error) {
    // Storage restrictions must never prevent normal shopping.
  }

  window.addEventListener('pageshow', event => {
    const saved = event.persisted ? readSnapshot() : restored;
    productDeparture = null;
    restored = null;
    if (!saved) return;
    if (!event.persisted) {
      window.renderSelects?.();
      window.loadDesktopOnlyTemplates?.();
      window.stickersReinit?.();
    }
    // With bfcache, keep the existing product DOM and only correct its position.
    restorePosition(saved);
  });

  function saveSnapshot() {
    // Do not save a partially updated filter result under the new filter URL.
    if (container.querySelector('.collection.loading')) return;
    try {
      const key = entryKey();
      const copy = container.cloneNode(true);
      // A navigation may occur while Show more is still fetching.
      const button = copy.querySelector('.js-pagination__next-button[href]');
      if (button) {
        button.removeAttribute('aria-disabled');
        const label = container.closest('infinite-scroll')?.originalButtonText;
        if (label) button.textContent = label;
      }
      const departure = productDeparture && productDeparture.url === url() &&
        Date.now() - productDeparture.time < 10000 ? productDeparture : null;
      memorySnapshot = {
        url: url(), time: Date.now(), y: departure ? departure.y : window.scrollY,
        anchor: departure?.anchor, offset: departure?.offset, html: copy.innerHTML
      };
      // Bound storage use; remove stale entries before writing the newest one.
      const keys = Object.keys(sessionStorage).filter(item => item.startsWith(prefix));
      const entries = keys.map(item => {
        try { return { key: item, time: JSON.parse(sessionStorage.getItem(item)).time }; }
        catch (error) { return { key: item, time: 0 }; }
      }).sort((a, b) => b.time - a.time);
      entries.forEach((entry, index) => {
        if (index >= 4 || Date.now() - entry.time >= maxAge) sessionStorage.removeItem(entry.key);
      });
      sessionStorage.setItem(key, JSON.stringify(memorySnapshot));
    } catch (error) {
      // Private browsing or a full storage quota falls back to normal navigation.
    }
  }

  window.addEventListener('pagehide', () => {
    stopRestore();
    saveSnapshot();
  });
})();
